<template>
  <div
    class="dui-dropdown"
    :class="{ 'empty': (!search && isEmpty),
              'multiple': multiple,
              'no-selection': !vm.selectedOption,
              'open': vm.isOpen }"
    @click="toggleOpen"
  >
    <slot name="options" />

    <input
      v-if="!vm.removeSelected"
      :name="name"
      type="hidden"
      :value="vm.value"
    >

    <span class="label-and-text">
      <span
        v-if="label"
        class="dropdown-label"
        :style="{ width: labelWidth + 'px' }"
      >{{ label }}</span>

      <span
        class="text"
        :title="selectText"
      >
        {{ selectText }} <DuiIconDown />
      </span>
    </span>

    <div
      class="options-container"
      :class="{ 'search': search }"
    >
      <!-- Search text -->
      <span
        v-if="search"
        class="search-text"
        :title="searchPlaceholder"
        @click.stop.prevent
      >
        <input
          ref="searchInput"
          v-model="vm.searchText"
          :placeholder="searchPlaceholder"
          @focus="toggleOpen(true)"
          @input="textSearchPerformed"
        >
        <button
          v-if="(vm.isOpen && vm.searchText)"
          class="button-sm button-icon button-close"
          @click.stop.prevent="clearSearchText"
        />
      </span>
      <!-- END Search text -->

      <div
        v-if="isEmpty"
        class="options-list"
      >
        <span class="no-results">No results</span>
      </div>

      <div
        v-if="!isEmpty"
        class="options-list"
      >
        <DuiDropdownOption
          v-for="option in enabledOptions"
          :key="option.idx"
          :class="{ 'option-label': option.isLabel,
                    'selected': option.selected }"
          :multiple="multiple"
          :option="option"
          @option-selected="optionSelectionChanged"
        />
      </div>
    </div>
  </div>
</template>

<script>
import KEY_CODES from '@dui/helpers/key-codes';
import storeKey from '@dui/mixins/store-key';

const EMPTY_TEXT = 'No options available.';

function _getFilteredSearchText(text) {
  if (!text) {
    return '';
  }

  return text.toLowerCase().replace(/([^a-z\s\&])/g, '');
}

function _getOptionByValue(options, value) {
  if (!options ||
    value === null ||
    typeof value === 'undefined') {
    return null;
  }

  let i = 0;
  const len = options.length;
  let optionFound = null;

  while (!optionFound && i < len) {
    const option = options[i];
    if (!option.isLabel &&
      option.value.toString() === value.toString()) {
      return option;
    }

    optionFound = _getOptionByValue(option.options, value);
    i++;
  }

  return optionFound;
}

function _getOptionsFromElement(parentEl, options, isNested) {
  const optionEls = parentEl.querySelectorAll('.dui-option');

  [].forEach.call(optionEls, (optionEl) => {
    if (optionEl.parentElement !== parentEl) {
      return;
    }

    const option = {
      isLabel: optionEl.dataset.isLabel === 'true',
      options: [],
      text: optionEl.dataset.text,
      value: optionEl.dataset.value,
    };

    if (isNested && option.options) {
      _getOptionsFromElement(optionEl, option.options, isNested);
    }

    optionEl.remove();
    options.push(option);
  });
}

function _getSelectedOptionsCount(options) {
  if (!Array.isArray(options)) {
    return 0;
  }

  let count = 0;

  options.forEach((option) => {
    let childCount = _getSelectedOptionsCount(option.options);
    let selfCount = (option.selected === true);
    count += Math.max(childCount, selfCount);
  });

  return count;
}

export default {
  mixins: [
    storeKey,
  ],
  props: {
    emptyText: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    labelWidth: {
      type: Number,
      default: null,
    },
    maxOptions: {
      type: Number,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: null,
    },
    nested: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => null,
    },
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    removeSelected: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    value: {
      type: [ Number, String ],
      default: null,
    },
  },
  data: () => ({
    vm: {
      isDisabled: false,
      isOpen: false,
      options: [],
      searchText: null,
      selectedOption: null,
      value: null,
      text: '',
    },
  }),
  computed: {
    enabledOptions() {
      return this.vm.options.filter(option => option.enabled !== false);
    },
    isEmpty() {
      return (this.enabledOptions.length === 0);
    },
    selectText() {
      if (this.isEmpty) {
        return this.getEmptyText();
      }
      if (this.vm.selectedOption) {
        return this.vm.selectedOption.text;
      }
      return this.placeholder;
    },
    storeOptions() {
      return this.loadFromStore();
    },
  },
  watch: {
    options: function(newVal) {
      if (newVal === this.vm.options) {
        return;
      }
      this.setInitialOptions();
    },
    storeOptions: function(newVal) {
      if (newVal === this.vm.options) {
        return;
      }
      this.setInitialOptions();
    },
    value: function(newVal) {
      const option = _getOptionByValue(this.vm.options, newVal);
      if (!option) {
        this.deselectAll();
        return;
      }

      this.selectOption(option);
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  mounted() {
    this.setInitialOptions();
  },
  methods: {
    clearSearchText() {
      if (!this.search) {
        return;
      }

      this.vm.searchText = '';
      this.textSearchPerformed();
      this.setSearchInputFocus();
    },
    deselectAll() {
      this.vm.options.forEach((option) => {
        this.optionSelectionChanged(option, false, false);
      });

      if (this.vm.selectedOption) {
        this.selectOption(this.vm.selectedOption, false);
        this.vm.selectedOption = null;
      }
    },
    deselectChildren(option) {
      if (!option || !option.options) {
        return;
      }

      option.options.forEach((innerOption) => {
        if (!innerOption.selected) {
          return;
        }

        this.$set(innerOption, 'selected', false);

        if (this.storeUpdateKey) {
          this.$store.dispatch(this.storeUpdateKey, innerOption);
        }

        this.deselectChildren(innerOption);
      });
    },
    getEmptyText() {
      return this.emptyText || EMPTY_TEXT;
    },
    getOptions() {
      if (this.storeLoadKey) {
        return this.storeOptions;
      }

      if (this.options) {
        return this.options;
      }

      const options = [];
      _getOptionsFromElement(this.$el, options, this.nested);
      return options;
    },
    optionSelectionChanged(option, isSelected = true, shouldToggle = true) {
      if (this.vm.selectedOption && !this.multiple) {
        this.saveOptionSelectedState(this.vm.selectedOption, false);
      }

      this.updateDisabledState();

      if (this.nested && isSelected === false) {
        this.deselectChildren(option);
      }

      this.selectOption(option, isSelected);

      if (this.multiple || !shouldToggle) {
        return;
      }

      this.toggleOpen();
    },
    saveOptionSelectedState(option, isSelected) {
      this.$set(option, 'selected', isSelected);
      this.updateDisabledState();

      if (!this.storeUpdateKey) {
        return;
      }

      this.$store.dispatch(this.storeUpdateKey, option);
    },
    selectOption(option, isSelected = true) {
      if (option.isLabel || (isSelected && this.vm.isDisabled)) {
        return;
      }

      this.saveOptionSelectedState(option, isSelected);

      if (isSelected === true && this.removeSelected) {
        option.enabled = false;
        return;
      }

      if (this.multiple) {
        return;
      }

      if (this.vm.selectedOption && this.vm.selectedOption.value === option.value) {
        return;
      }

      const selectedOptionValue = isSelected ? option.value : null;
      this.$emit('option-selected', option);
      this.$emit('input', selectedOptionValue);
      this.$emit('change');

      if (!isSelected) {
        return;
      }

      this.vm.selectedOption = option;
      this.vm.value =  this.vm.selectedOption.value;
      this.vm.text = this.vm.selectedOption.text;
    },
    setInitialOptions() {
      this.vm.options = this.getOptions();

      this.vm.options.forEach((option, idx) => {
        const isSelected = option.value && (option.value.toString() ===  this.value);
        this.$set(option, 'enabled', option.enabled !== false);
        this.$set(option, 'idx',  idx);

        if (isSelected) {
          this.selectOption(option);
        }
      });

      if (this.storeSaveKey) {
        this.$store.dispatch(this.storeSaveKey, this.vm.options);
      }
    },
    setSearchEnabledOptions(options, searchText, isSelectionForced = false) {
      if (!options) {
        return false;
      }

      let isEnabled = false;

      options.forEach((option) => {
        if (!searchText) {
          option.enabled = true;
        }

        let isSelfAMatch = isSelectionForced || (option.text.toLowerCase().indexOf(searchText) > -1);
        let isChildEnabled = this.setSearchEnabledOptions(option.options, searchText, isSelfAMatch);
        let isSelfEnabled = isChildEnabled || isSelfAMatch;

        this.$set(option, 'enabled', isSelfEnabled);

        isEnabled = isEnabled || isSelfEnabled || isChildEnabled;
      });

      return isEnabled;
    },
    setSearchInputFocus() {
      if (!this.search) {
        return;
      }

      if (this.vm.isOpen) {
        this.$refs.searchInput.focus();
        return;
      }

      this.$refs.searchInput.blur();
    },
    toggleOpen(openState) {
      if (typeof openState === 'boolean') {
        this.vm.isOpen = openState;
      }
      else {
        this.vm.isOpen = !this.vm.isOpen;
      }

      this.setSearchInputFocus();

      if (this.vm.isOpen) {
        this.addEventListeners();
        return;
      }

      this.removeEventListeners();
    },
    updateDisabledState() {
      if (!this.maxOptions) {
        return;
      }

      const maxOptions = parseInt(this.maxOptions);
      const numSelectedOptions = _getSelectedOptionsCount(this.vm.options);
      this.vm.isDisabled = (numSelectedOptions >= maxOptions);

      if (this.vm.isDisabled) {
        this.toggleOpen(false);
      }
    },
    // Events
    addEventListeners() {
      document.addEventListener('mousedown', this.windowClickPerformed);
      window.addEventListener('keydown', this.keyUpPerformed);
    },
    removeEventListeners() {
      document.removeEventListener('mousedown', this.windowClickPerformed);
      window.removeEventListener('keydown', this.keyUpPerformed);
    },
    keyUpPerformed(ev) {
      if (ev.keyCode !== KEY_CODES.ESCAPE) {
        return;
      }

      this.toggleOpen(false);
    },
    textSearchPerformed() {
      if (!this.search) {
        return;
      }

      this.setSearchEnabledOptions(this.vm.options, _getFilteredSearchText(this.vm.searchText));
    },
    windowClickPerformed(event) {
      if (event.target === this.$el || this.$el.contains(event.target)){
        return;
      }

      this.toggleOpen(false);
    },
    // END Events
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

$dui-dropdown-max-height: 250px;

@mixin makeDropdownLevels($limit: 6) {
  $selector: '> .dui-dropdown-option';
  $current: $selector;
  @for $i from 1 through $limit {
    #{ $current } {
      > .content {
        padding-left: ($i * $_1-5em);
      }
    }

    $current: $current + ' ' + $selector;
  }
}

.dui-dropdown {
  background: getColor($white);
  border: $border-width-base solid getColor($grey, fade);
  border-radius: $border-radius-base;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  line-height: 1.25;
  position: relative;
  width: 100%;

  @include transition((
    border
  ));

  &:hover,
  &.open {
    border-color: getColor($grey, dark);
  }

  &.invalid {
    border-color: $color-invalid;
  }

  option {
    display: none;
  }

  &[disabled],
  &.empty,
  &.no-selection,
  &.open {
    > .label-and-text .text {
      color: getColor($grey, dark);
    }
  }

  &[disabled] {
    opacity: $disabled-opacity;
  }

  &[disabled],
  &.empty {
    pointer-events: none;
  }


  > .label-and-text {
    border-radius: $border-radius-base;
    display: flex;
    padding: $_1-5em $_2em;

    .dropdown-label {
      border-right: $border-width-base solid getColor($grey, fade);
      flex: 0 1 auto;
      font-weight: $base-weight-bold;
      margin-right: ($_1em + $border-width-base);
      padding-right: $_1em;
    }

    .text {
      flex: 1;
      padding-right: $_3em;
      position: relative;

      @include ellipsis();

      @include transition((
        color
      ));

      .dui-icon {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  &.sm > .label-and-text {
    padding: $_1em $_2em;
  }

  .options-container {
    background-color: getColor($white);
    border: $border-width-base solid getColor($grey, fade);
    border-radius: $border-radius-base;
    box-shadow: $box-shadow-context-menu;
    left: -$border-width-base;
    line-height: 1.5;
    opacity: 0;
    padding: $_0-5em;
    pointer-events: none;
    position: absolute;
    top: 100%;
    width: calc(100% - #{ $_1em });
    z-index: $z-index-form-elements;

    @include transition((
      opacity, top
    ));

    > .search-text {
      background-color: getColor($grey);
      border-radius: $border-radius-lg;
      display: flex;
      margin: $_1em;
      padding: 0 $_5em 0 $_2em;
      position: relative;

      input {
        background-color: getColor($grey);
        border: none;
        box-sizing: border-box;
        font-size: $font-size-base;
        line-height: 1.25;
        outline: none;
        padding: $_1-5em 0;
        width: 100%;
      }

      .button-close {
        position: absolute;
        right: $_1-5em;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .options-list {
      max-height: $dui-dropdown-max-height;
      overflow-y: hidden;

      @include scrollBar();

      // Recursive options
      @include makeDropdownLevels();

      > .no-results {
        color: getColor($grey, dark);
        display: flex;
        flex: 1;
        padding: $_1em $_1-5em;
        pointer-events: none;
      }
    }
  }

  &.open .options-container {
    opacity: 1;
    pointer-events: all;
    top: calc(100% + #{ $_0-5em + $border-width-base });

    .options-list {
      overflow-y: auto;
    }
  }

  &.multiple {
    > .dui-dropdown-option > .content {
      font-weight: $base-weight-bold;
    }
  }

  .dui-dropdown-option {
    &.option-label > .content {
      color: getColor($grey, dark);
      cursor: default;
      font-weight: $base-weight-bold;

      &:hover {
        background-color: getColor($white);
      }
    }

    &.selected > .content {
      background-color: getColor($yellow, fade);

      .dui-icon {
        opacity: 1;
      }
    }

    .dui-dropdown-option {
      display: flex;
      flex-direction: column;
    }

    > .content {
      background-color: getColor($white);
      color: getColor($black);
      cursor: pointer;
      display: flex;
      flex: 1;
      padding: $_1em $_3em $_1-5em $_1em;
      position: relative;

      .dui-icon {
        opacity: 0;
        position: absolute;
        right: $_1em;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;

        @include transition((
          opacity
        ));
      }

      @include transition((
        background-color
      ));

      &:hover {
        background-color: getColor($grey);
      }

      > .text {
        @include ellipsis();
      }
    }
  }

  &.dropdown-grey .dui-dropdown-option > .content:hover {
    background-color: getColor($grey, fade);
  }

  &.upwards {
    @include duiDropdownUpwards();
  }
}
</style>
